"use client"

import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, MessageSquareHeart } from "lucide-react"
import { useFabCorner, type FabCorner } from "@/hooks/use-fab-corner"
import { useFabAutoCorner } from "@/hooks/use-fab-auto-corner"
import { useAuthStore } from "@/stores/auth-store"
import { useTranslation } from "@/lib/i18n"
import { cn } from "@/lib/utils"

// ─── Corner-snap Feedback FAB (primary position — closer to edge) ────────────
export function FeedbackFab({ onOpen, unreadCount }: { onOpen: () => void; unreadCount: number }) {
  const { t } = useTranslation()
  const translatedLabel = t("feedbackWidget.submitFeedback")
  const label = translatedLabel === "feedbackWidget.submitFeedback" ? "Submit Feedback" : translatedLabel
  const fab = useFabCorner()
  // Chevron handles + anchor edges follow the EFFECTIVE corner so they
  // line up with the host's animated position, not the operator's saved
  // preference. Saved preference still drives `fab.setCorner` writes.
  const { effectiveCorner } = useFabAutoCorner()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [mounted, setMounted] = useState(false)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const [handlesVisible, setHandlesVisible] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showHandles = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
    setHandlesVisible(true)
  }
  const scheduleHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => setHandlesVisible(false), 400)
  }
  useEffect(() => {
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current) }
  }, [])

  const [badge, setBadge] = useState(unreadCount)
  useEffect(() => {
    const handler = (e: Event) => setBadge((e as CustomEvent).detail || 0)
    window.addEventListener("feedback:unread-count", handler)
    return () => window.removeEventListener("feedback:unread-count", handler)
  }, [])

  // Resolve the portal target (created by FabStackHost). Retry one tick if the
  // host mounts after us on a given route.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setMounted(true)
    const lookup = () => setHost(document.getElementById("fab-stack-root"))
    lookup()
    if (!document.getElementById("fab-stack-root")) {
      const t = setTimeout(lookup, 0)
      return () => clearTimeout(t)
    }
  }, [])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleClick = () => {
    if (!fab.wasDrag()) onOpen()
  }

  if (!isAuthenticated) return null
  if (!mounted || !host) return null

  // Anchor: Heart sits AT the corner edge (no translate). The two
  // directional chevron handles peek out 50px on the perpendicular axes.
  const anchorMap: Record<FabCorner, string> = {
    "top-left":     "top-0 left-0",
    "top-right":    "top-0 right-0",
    "bottom-left":  "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  }
  const anchorEdge = anchorMap[effectiveCorner]

  const isBottom = effectiveCorner.startsWith("bottom")
  const isRight = effectiveCorner.endsWith("right")

  // Vertical handle snaps the FAB to the OPPOSITE vertical corner on the
  // same horizontal side; horizontal handle snaps to the OPPOSITE horizontal
  // corner on the same vertical side.
  const verticalTarget: FabCorner = isBottom
    ? (isRight ? "top-right" : "top-left")
    : (isRight ? "bottom-right" : "bottom-left")
  const horizontalTarget: FabCorner = isRight
    ? (isBottom ? "bottom-left" : "top-left")
    : (isBottom ? "bottom-right" : "top-right")

  const VerticalIcon = isBottom ? ChevronUp : ChevronDown
  const HorizontalIcon = isRight ? ChevronLeft : ChevronRight

  // Vertical handle: 60px above (bottom corner) / below (top corner) Heart.
  const verticalOffset = isBottom ? "-translate-y-[60px]" : "translate-y-[60px]"
  // Horizontal handle: 60px to the LEFT (right corner) / RIGHT (left corner) of Heart.
  const horizontalOffset = isRight ? "-translate-x-[60px]" : "translate-x-[60px]"

  const verticalLabels: Record<FabCorner, string> = {
    "top-left": "top left",
    "top-right": "top right",
    "bottom-left": "bottom left",
    "bottom-right": "bottom right",
  }

  return createPortal(
    <>
      {!fab.isDragging && (
        <>
          <button
            type="button"
            aria-label={`Move feedback to ${verticalLabels[verticalTarget]}`}
            onPointerEnter={showHandles}
            onPointerLeave={scheduleHide}
            onClick={(e) => {
              e.stopPropagation()
              fab.setCorner(verticalTarget)
            }}
            className={cn(
              "absolute w-6 h-6 rounded-full flex items-center justify-center z-10",
              anchorEdge,
              verticalOffset,
              "bg-background text-foreground",
              "border-2 border-foreground transition-opacity duration-200",
              handlesVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            )}
            style={{
              marginRight: isRight ? 12 : undefined,
              marginLeft: !isRight ? 12 : undefined,
            }}
          >
            <VerticalIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={`Move feedback to ${verticalLabels[horizontalTarget]}`}
            onPointerEnter={showHandles}
            onPointerLeave={scheduleHide}
            onClick={(e) => {
              e.stopPropagation()
              fab.setCorner(horizontalTarget)
            }}
            className={cn(
              "absolute w-6 h-6 rounded-full flex items-center justify-center z-10",
              anchorEdge,
              horizontalOffset,
              "bg-background text-foreground",
              "border-2 border-foreground transition-opacity duration-200",
              handlesVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            )}
            style={{
              marginBottom: isBottom ? 12 : undefined,
              marginTop: !isBottom ? 12 : undefined,
            }}
          >
            <HorizontalIcon className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      <div
        className={cn("group/feedback-fab absolute w-12 h-12", anchorEdge)}
        style={
          fab.dragPos
            ? { position: "fixed", left: fab.dragPos.x - 24, top: fab.dragPos.y - 24, bottom: "auto", right: "auto" }
            : undefined
        }
      >
        {/* Keep resting opacity outside the animated element: its breathe
            keyframes also set opacity. The halo exists only with the FAB.
            It stays lit at rest — it is permanent chrome, not a hover
            affordance, and a touch device never produces a hover at all;
            hover only brightens it. */}
        <span
          aria-hidden="true"
          className="absolute -inset-3 pointer-events-none opacity-80 group-hover/feedback-fab:opacity-100 transition-opacity duration-[380ms] ease-out motion-reduce:transition-none"
        >
          <span className="block h-full w-full rounded-[32px] pip-aura-active motion-reduce:animate-none!" />
        </span>
        <button
          aria-label={label}
          aria-haspopup="dialog"
          className={cn(
            "relative w-12 h-12 rounded-full flex items-center justify-center z-10",
            "bg-background text-foreground border border-border shadow-sm",
            "pointer-events-auto",
            fab.isDragging && "opacity-70",
          )}
          {...fab.dragHandlers}
          onPointerEnter={showHandles}
          onPointerLeave={scheduleHide}
          onClick={handleClick}
          title={label}
        >
          <MessageSquareHeart className="h-5 w-5" />
          {badge > 0 && (
            <span className="absolute -top-1 -end-1 h-4 min-w-4 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1">
              {badge}
            </span>
          )}
        </button>
      </div>
    </>,
    host,
  )
}
